// @flow
import {
  fetchCategory as apiFetchCategory,
  fetchSubsiteCategories as apiFetchSubsiteCategories,
  deleteCategory as apiDeleteCategory
} from 'api/cms';
import type { Category } from 'api/cms';
import type { Dispatch, GetState } from '../..';
import { setError } from '../../../main/actions/error';

type FetchCategoryRequestAction = {
  type: 'CMS_DASHBOARD_FETCH_CATEGORY_REQUEST',
  categoryId: string
};
type FetchCategorySuccessAction = {
  type: 'CMS_DASHBOARD_FETCH_CATEGORY_SUCCESS',
  subsiteId: string,
  category: Category
};
type FetchSubsiteCategoriesRequestAction = {
  type: 'CMS_DASHBOARD_FETCH_CATEGORIES_REQUEST',
  subsiteId: string
};
type FetchSubsiteCategoriesSuccessAction = {
  type: 'CMS_DASHBOARD_FETCH_CATEGORIES_SUCCESS',
  subsiteId: string,
  categories: Array<Category>
};

export type Action =
  | FetchCategoryRequestAction
  | FetchCategorySuccessAction
  | FetchSubsiteCategoriesRequestAction
  | FetchSubsiteCategoriesSuccessAction;

const fetchCategoryRequest = (
  categoryId: string
): FetchCategoryRequestAction => ({
  type: 'CMS_DASHBOARD_FETCH_CATEGORY_REQUEST',
  categoryId
});

const fetchCategorySuccess = (
  subsiteId: string,
  category: Category
): FetchCategorySuccessAction => ({
  type: 'CMS_DASHBOARD_FETCH_CATEGORY_SUCCESS',
  category,
  subsiteId
});

export const fetchCategory = (categoryId: string, subsiteId: string) => (
  dispatch: Dispatch,
  getState: GetState
) => {
  const state = getState();
  const category = state.cmsDashboard.categories.byId[categoryId];

  if (!category) {
    dispatch(fetchCategoryRequest(categoryId));
    return apiFetchCategory(categoryId)
      .then(fetchedCategory => {
        dispatch(fetchCategorySuccess(subsiteId, fetchedCategory));
      })
      .catch(error => {
        dispatch(setError(error));
      });
  }
};

export const updateCategory = fetchCategorySuccess;

const fetchSubsiteCategoriesRequest = (
  subsiteId: string
): FetchSubsiteCategoriesRequestAction => ({
  type: 'CMS_DASHBOARD_FETCH_CATEGORIES_REQUEST',
  subsiteId
});

const fetchSubsiteCategoriesSuccess = (
  subsiteId: string,
  categories: Array<Category>
): FetchSubsiteCategoriesSuccessAction => ({
  type: 'CMS_DASHBOARD_FETCH_CATEGORIES_SUCCESS',
  subsiteId,
  categories
});

export const fetchSubsiteCategories = (subsiteId: string) => (
  dispatch: Dispatch,
  getState: GetState
) => {
  const state = getState();
  const subsiteCategories =
    state.cmsDashboard.categories.bySubsiteId[subsiteId];

  if (!subsiteCategories) {
    dispatch(fetchSubsiteCategoriesRequest(subsiteId));
    apiFetchSubsiteCategories(subsiteId)
      .then(fetchedCategories => {
        dispatch(fetchSubsiteCategoriesSuccess(subsiteId, fetchedCategories));
      })
      .catch(error => {
        dispatch(setError(error));
      });
  }
};

export const updateCategories = fetchSubsiteCategoriesSuccess;

export const deleteCategory = (categoryId: string, subsiteId: string) => (
  dispatch: Dispatch,
  getState: GetState
) => {
  const { byId, bySubsiteId } = getState().cmsDashboard.categories;
  return apiDeleteCategory(categoryId)
    .then(() => {
      const categoryIds = bySubsiteId[subsiteId].categoryIds.filter(
        cid => cid !== categoryId
      );
      const categories = categoryIds.map(cid => byId[cid]);
      const filteredCategories = categories.map(category => {
        if (category.parentId === categoryId) {
          category.parentId = null;
        }
        const filteredChildrenIds = category.childrenIds.filter(
          cid => cid !== categoryId
        );
        return { ...category, childrenIds: filteredChildrenIds };
      });
      dispatch(updateCategories(subsiteId, filteredCategories));
    })
    .catch(setError);
};
